import { useCallback, useEffect, useState } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { AlertCircle, CheckCircle, Info, Loader2, X } from 'lucide-react'
import { cn } from '@shared/lib/cn'
import { toastEmitter } from '@shared/lib/toastEmitter'
import { ToastContext } from './ToastContext'
import { ToastVariant, type ToastItem } from './types'

const MAX_TOASTS = 3
const EXIT_DURATION_MS = 200
const DEFAULT_DURATION_MS = 4000

type ToastState = ToastItem & { open: boolean }

const variantConfig: Record<ToastVariant, { icon: React.ReactNode; classes: string }> = {
  [ToastVariant.success]: {
    icon: <CheckCircle className="h-5 w-5 text-green-600" />,
    classes: 'border-green-200 bg-green-50',
  },
  [ToastVariant.error]: {
    icon: <AlertCircle className="h-5 w-5 text-red-600" />,
    classes: 'border-red-200 bg-red-50',
  },
  [ToastVariant.info]: {
    icon: <Info className="h-5 w-5 text-blue-600" />,
    classes: 'border-blue-200 bg-blue-50',
  },
  [ToastVariant.loading]: {
    icon: <Loader2 className="h-5 w-5 animate-spin text-blue-600" />,
    classes: 'border-blue-200 bg-blue-50',
  },
}

// A `loading` toast stays open until it is updated/dismissed and hides its close
// button; everything else auto-dismisses and is closable, unless overridden.
const isToastDismissible = (t: ToastItem): boolean =>
  t.dismissible ?? t.variant !== ToastVariant.loading

const toastDuration = (t: ToastItem): number =>
  t.duration ?? (isToastDismissible(t) ? DEFAULT_DURATION_MS : Infinity)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastState[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)))
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, EXIT_DURATION_MS)
  }, [])

  const toast = useCallback(
    (item: Omit<ToastItem, 'id'>) => {
      const id = Math.random().toString(36).slice(2)
      setToasts((prev) => {
        const next = [...prev, { ...item, id, open: true }]
        if (next.length <= MAX_TOASTS) return next
        // Evict the oldest *dismissible* toast so an in-progress (non-dismissible)
        // toast is never silently dropped while it is still tracking work.
        const evicted = next.find((t) => isToastDismissible(t))
        if (!evicted) return next
        setTimeout(() => dismiss(evicted.id), 0)
        return next.filter((t) => t.id !== evicted.id)
      })
      return id
    },
    [dismiss]
  )

  const update = useCallback((id: string, patch: Partial<Omit<ToastItem, 'id'>>) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)))
  }, [])

  useEffect(() => {
    toastEmitter.register(toast)
    return () => toastEmitter.unregister()
  }, [toast])

  return (
    <ToastContext.Provider value={{ toast, update, dismiss }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => {
          const config = variantConfig[t.variant ?? ToastVariant.info]
          const dismissible = isToastDismissible(t)
          return (
            <ToastPrimitive.Root
              key={t.id}
              open={t.open}
              // Ignore swipe/escape on non-dismissible toasts; only our own
              // dismiss()/update() can take them down.
              onOpenChange={(open) => {
                if (!open && dismissible) dismiss(t.id)
              }}
              duration={toastDuration(t)}
              className={cn(
                'toast-root flex w-80 items-start gap-3 rounded-xl border p-4 shadow-md',
                config.classes
              )}
            >
              {config.icon}
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-sm font-semibold text-gray-900">
                  {t.title}
                </ToastPrimitive.Title>
                {t.description && (
                  <ToastPrimitive.Description className="mt-0.5 text-xs text-gray-600">
                    {t.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              {dismissible && (
                <ToastPrimitive.Close className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700">
                  <X className="h-4 w-4" />
                </ToastPrimitive.Close>
              )}
            </ToastPrimitive.Root>
          )
        })}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-100 flex flex-col gap-2" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}
